/**
 * ButtonGroup
 *
 * This component is built to WAI-ARIA specifications for radio buttons while being styled as buttons
 * @see {@link https://www.w3.org/TR/2017/WD-wai-aria-practices-1.1-20170628/examples/radio/radio-1/radio-1.html}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/radio_role}
 */
import { DOMAttributes, ReactNode } from "react";
import { TFuncKey } from "react-i18next";

import Button from "components/Button";
import Flex from "components/Flex";
import { FlexProps } from "components/Flex/Flex";
import Text from "components/Text";

import { useClientTranslation } from "hooks/useClientTranslation";

export type Option<Value> = {
  label: string;
  value: Value;
};

export type ButtonGroupProps<GroupName, Value> = {
  label?: ReactNode;
  options: Option<Value>[];
  onChange: (selectedValue: Value) => void;
  name: GroupName;
  value: Value;
  description?: ReactNode;
  onBlur?: DOMAttributes<HTMLDivElement>["onBlur"];
} & Omit<FlexProps, "children">;

export const ButtonGroup = <GroupName extends string, Value extends boolean | string | undefined>({
  options,
  onChange,
  label,
  name,
  value,
  description,
  onBlur,
  ...containerProps
}: ButtonGroupProps<GroupName, Value>) => {
  const { t } = useClientTranslation();

  return (
    <Flex gap={16} {...containerProps}>
      {label && <label id={name}>{label}</label>}
      {description && <Text>{description}</Text>}
      <Flex gap={16} direction="row" role="radiogroup" onBlur={onBlur} aria-labelledby={name}>
        {options.map((option) => {
          const text = t(option.label as TFuncKey<"default">);

          return (
            <Button
              style={{ width: "100px" }}
              role="radio"
              aria-checked={value === option.value}
              aria-labelledby={`${name}-${text}-label`}
              key={text}
              onClick={() => onChange(option.value)}
              typeVariant={value === option.value ? "primary" : "secondary"}
            >
              <label id={`${name}-${text}-label`}>{text}</label>
            </Button>
          );
        })}
      </Flex>
    </Flex>
  );
};
